import { Status } from "@/constants/productStock";

// Clipboard text copied from Excel is tab-separated columns, newline-separated
// rows. Expected columns: product code | quantity (optional) | unit (optional).
// A column of codes alone is fine: the rows are added and the user fills in
// the quantities.

export const PASTE_REASONS = {
  NOT_FOUND: "ไม่พบสินค้า",
  BLOCKED: "สินค้านี้ถูกบล็อก ไม่สามารถเลือกได้",
  BAD_QTY: "จำนวนต้องเป็นจำนวนเต็มที่มากกว่า 0",
  NO_UNIT: "ไม่พบหน่วยสินค้านี้ในสินค้า",
};

// Only treat a paste as a table when it clearly came from a spreadsheet, so
// pasting a plain word into a search box keeps working normally.
export function isTablePaste(text) {
  return typeof text === "string" && /[\t\n]/.test(text.replace(/\r?\n$/, ""));
}

// Minimal TSV parser: handles Excel's quoted cells ("a ""b""\nc").
export function parseTsv(text) {
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;
  const src = text.replace(/\r\n?/g, "\n");

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"' && src[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"' && cell === "") {
      inQuotes = true;
    } else if (ch === "\t") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += ch;
    }
  }
  row.push(cell);
  rows.push(row);

  return rows
    .map(r => r.map(c => c.trim()))
    .filter(r => r.some(c => c !== ""));
}

// Spreadsheet cells often carry invisible junk (NBSP, zero-width spaces,
// full-width characters), so compare in a normalised form.
const norm = value =>
  String(value ?? "")
    .normalize("NFKC")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

// Excel drops leading zeros from number-looking codes ("00123" -> "123").
const stripLeadingZeros = key => (/^\d+$/.test(key) ? key.replace(/^0+(?=\d)/, "") : key);

const parseQty = raw => {
  const cleaned = String(raw ?? "").replace(/[,\s]/g, "");
  if (cleaned === "") return { qty: null }; // codes only, qty filled in later
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
    return { error: PASTE_REASONS.BAD_QTY };
  }
  return { qty: n };
};

const findProduct = (productList, text, { strict = false } = {}) => {
  const key = norm(text);
  if (!key) return null;

  const exact =
    productList.find(p => norm(p.productCode) === key) ||
    productList.find(p => norm(p.productNameMain) === key);
  if (exact) return exact;

  // "CODE - Name", the way the product dropdown shows it
  const sep = key.indexOf(" - ");
  if (sep > 0) {
    const byLabel = productList.find(
      p => norm(p.productCode) === key.slice(0, sep).trim()
    );
    if (byLabel) return byLabel;
  }

  if (strict) return null;

  // Same code once leading zeros are ignored. Only when it points at exactly
  // one product, so we never guess between two.
  const loose = stripLeadingZeros(key);
  const candidates = productList.filter(
    p => stripLeadingZeros(norm(p.productCode)) === loose
  );
  return candidates.length === 1 ? candidates[0] : null;
};

// All units this product can be bought in: base unit first, then conversions.
export function getProductUnits(product) {
  const units = [];
  const add = uom => {
    if (uom && uom.id && !units.find(u => u.id === uom.id)) units.push(uom);
  };
  add(product.productUnit);
  for (const c of product.productConversionUnitItems || []) {
    add(c.uom || c.productSubUnit);
  }
  return units;
}

const toUnit = uom => ({ id: uom.id, name: uom.description });

const findUnit = (product, text) => {
  const units = getProductUnits(product);
  if (!norm(text)) return units.length ? toUnit(units[0]) : null;
  const key = norm(text);
  const match = units.find(
    u =>
      norm(u.description) === key ||
      norm(u.description2) === key ||
      norm(u.code) === key
  );
  return match ? toUnit(match) : null;
};

// Turn pasted text into valid lines plus the rows that had to be skipped.
// `rowNo` is the row's position in the pasted block (1-based) so the user can
// find it in their sheet.
export function parsePrPaste(text, productList) {
  const rows = parseTsv(text);
  const lines = [];
  const skipped = [];
  const byProduct = new Map();

  rows.forEach((cells, index) => {
    const rowNo = index + 1;
    // The code is usually the first column, but the sheet may start with a
    // "No." column, so look for the first cell that is exactly a known
    // product. Quantity and unit are then the two cells after it.
    const found = cells.findIndex(c =>
      findProduct(productList, c, { strict: true })
    );
    const at = found >= 0 ? found : 0;
    const [codeCell = "", qtyCell = "", unitCell = ""] = cells.slice(at);
    const product = findProduct(productList, codeCell);

    // First row that looks like column titles (not a product, qty not a number)
    if (
      index === 0 &&
      !product &&
      qtyCell !== "" &&
      !Number.isFinite(Number(qtyCell.replace(/[,\s]/g, "")))
    ) {
      return;
    }

    // Name the row by what looks like its code (not a "No." column) so the
    // user can find it in their sheet.
    const label = product
      ? codeCell
      : cells.find(c => c && !/^[\d.,\s]+$/.test(c)) || cells[0] || "";
    const skip = reason => skipped.push({ rowNo, text: label, reason });

    if (!product) return skip(PASTE_REASONS.NOT_FOUND);
    if (product.productStatus === Status.ARCHIVED) {
      return skip(PASTE_REASONS.BLOCKED);
    }
    const { qty, error } = parseQty(qtyCell);
    if (error) return skip(error);
    const unit = findUnit(product, unitCell);
    if (!unit) return skip(PASTE_REASONS.NO_UNIT);

    // Same product on several rows: add the quantities up (same unit only)
    const existing = byProduct.get(product.productId);
    if (existing && existing.unit.id === unit.id) {
      if (qty != null) existing.qty = (existing.qty ?? 0) + qty;
      return;
    }
    if (existing) return skip("สินค้าซ้ำแต่คนละหน่วย");
    const line = { product, qty, unit, rowNo };
    byProduct.set(product.productId, line);
    lines.push(line);
  });

  return { lines, skipped };
}

// Merge parsed lines into the table: empty placeholder rows are replaced, a
// product already in the table gets the pasted quantity (so pasting the same
// list twice doesn't double it, and a code pasted without a quantity leaves it
// alone), everything else is appended.
export function mergePastedLines(items, lines) {
  const next = items.filter(i => i.product);
  let added = 0;
  let updated = 0;
  let existing = 0;
  let needQty = 0;

  for (const { product, qty, unit } of lines) {
    const at = next.findIndex(i => i.product.productId === product.productId);
    if (at >= 0 && qty == null) {
      // codes only: the product is already there, keep what the user entered
      existing++;
      continue;
    }
    const item = {
      product,
      qty: qty ?? 0,
      unit,
      productTouched: false,
      qtyTouched: false,
    };
    if (at >= 0) {
      next[at] = item;
      updated++;
    } else {
      next.push(item);
      added++;
      if (qty == null) needQty++;
    }
  }
  return { items: next, added, updated, existing, needQty };
}
